import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import type { Bill } from "@luckymenu/billing-core";
import { type Kot, uuidv7 } from "@luckymenu/core";
import {
	type AssignPrintersInput,
	billDocument,
	kotCancelDocument,
	kotDocument,
	PRINT_NOT_PRINTED_AFTER_MS,
	type PrinterList,
	type PrintJob,
	type UpsertPrinterInput,
} from "@luckymenu/print-core";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { DiscoveredPrinter, PrinterConnection } from "../adapters";
import {
	type DiscoverProblem,
	discoverProblemOf,
	type PrintProblem,
	printProblemOf,
} from "../printing/errors";
import type { PrintOutcome, PrintServiceState } from "../printing/service";
import { useActiveRestaurant, useStaffCore, useSupportSession } from "../provider";
import { useCurrentMembership } from "./use-current";
import { useNow } from "./use-live-board";
import { useCan } from "./use-navigation";

/**
 * Printing for the screens of both staff apps (P5-T20 to P5-T28). The work
 * is done by the print service (printing/service.ts); these hooks start it,
 * show its state and run what people ask for.
 */

/** The device's print service, for actions outside these hooks. */
export function usePrintService() {
	return useStaffCore().print;
}

/** What this device knows: reach of each printer, auto print, jobs printing now, reports waiting. */
export function usePrintState(): PrintServiceState {
	const print = useStaffCore().print;
	return useSyncExternalStore(print.subscribe, print.getState, print.getState);
}

/**
 * Makes this device a print station while a restaurant is open (P5-T21):
 * both apps call it once, beside `useLiveConnection`. Restarts on a
 * restaurant switch. Not in an admin's support session: an admin's browser
 * never prints a restaurant's tickets. Prepares the logo for bills (P5-T27).
 */
export function usePrintStation(): void {
	const core = useStaffCore();
	const restaurantId = useActiveRestaurant((s) => s.restaurantId);
	const support = useSupportSession((s) => s.session);
	const current = useCurrentMembership();
	const logoFileId = current.data?.restaurant.logoFileId ?? null;
	const ready = restaurantId !== null && support === null && current.data !== undefined;
	useEffect(() => {
		if (!ready) return;
		void restaurantId;
		void core.print.start();
		return () => core.print.stop();
	}, [core, ready, restaurantId]);
	useEffect(() => {
		if (ready && logoFileId) void core.print.prepareLogo(logoFileId);
	}, [core, ready, logoFileId]);
}

export type PrinterAction = "save" | "delete" | "assign" | "test";

/**
 * More > Printers (MG-06, P5-T24, P5-T25): the printers with their status
 * from every device, where bills and each kitchen station's KOTs print and
 * how many copies, and saving, deleting, assigning and test prints. Changes
 * need `printers.manage`.
 */
export function usePrinters() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const core = useStaffCore();
	const canManage = useCan("printers.manage");
	const queryKey = useMemo(() => trpc.staff.printing.listPrinters.queryKey(), [trpc]);
	const query = useQuery(trpc.staff.printing.listPrinters.queryOptions());
	const [problem, setProblem] = useState<{ action: PrinterAction; problem: PrintProblem } | null>(
		null,
	);
	const [busy, setBusy] = useState<PrinterAction | null>(null);

	useEffect(() => {
		if (query.data) core.print.setPrinterList(query.data);
	}, [core, query.data]);

	const upsert = useMutation(trpc.staff.printing.upsertPrinter.mutationOptions());
	const remove = useMutation(trpc.staff.printing.deletePrinter.mutationOptions());
	const assign = useMutation(trpc.staff.printing.assignPrinters.mutationOptions());

	const run = useCallback(
		async <T>(action: PrinterAction, call: () => Promise<T>): Promise<T | null> => {
			setProblem(null);
			setBusy(action);
			try {
				return await call();
			} catch (error) {
				setProblem({ action, problem: printProblemOf(error) });
				return null;
			} finally {
				setBusy(null);
			}
		},
		[],
	);
	const refresh = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey });
	}, [queryClient, queryKey]);
	const upsertMutate = upsert.mutateAsync;
	const removeMutate = remove.mutateAsync;
	const assignMutate = assign.mutateAsync;

	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		list: query.data ?? null,
		retry: () => void query.refetch(),
		canManage,
		busy,
		problem,
		clearProblem: useCallback(() => setProblem(null), []),
		/** Adds (with a new ID) or changes a printer. */
		save: useCallback(
			(input: UpsertPrinterInput) =>
				run("save", async () => {
					const printer = await upsertMutate(input);
					await refresh();
					return printer;
				}),
			[run, upsertMutate, refresh],
		),
		remove: useCallback(
			(id: string) =>
				run("delete", async () => {
					await removeMutate({ id });
					await refresh();
					return true;
				}),
			[run, removeMutate, refresh],
		),
		assign: useCallback(
			(input: AssignPrintersInput) =>
				run("assign", async () => {
					const list = await assignMutate(input);
					queryClient.setQueryData<PrinterList>(queryKey, list);
					return list;
				}),
			[run, assignMutate, queryClient, queryKey],
		),
		/** A test page, printed here when this device reaches the printer. */
		testPrint: useCallback(
			(printerId: string) =>
				run("test", () => core.print.print({ kind: "test", refId: printerId, printerId })),
			[run, core],
		),
	};
}

/** A new printer's ID, made on the device so a retried save adds it once. */
export function newPrinterId(): string {
	return uuidv7();
}

/**
 * Printers found by this device's transports, for the add printer wizard
 * (P5-T24). `search(connection)` asks that transport only (Android asks for
 * the Bluetooth permission only once Bluetooth is chosen) and keeps why it
 * failed in `problem`.
 */
export function useDiscoverPrinters() {
	const core = useStaffCore();
	const [found, setFound] = useState<DiscoveredPrinter[] | null>(null);
	const [searching, setSearching] = useState(false);
	const [problem, setProblem] = useState<DiscoverProblem | null>(null);
	const search = useCallback(
		async (connection?: PrinterConnection) => {
			setSearching(true);
			setProblem(null);
			try {
				setFound(await core.print.discover(connection));
			} catch (error) {
				setFound([]);
				setProblem(discoverProblemOf(error));
			} finally {
				setSearching(false);
			}
		},
		[core],
	);
	return {
		found,
		searching,
		search,
		problem,
		/** Whether this device can search at all (it has a transport that discovers). */
		canSearch: core.platform.printTransports.some((t) => t.discover !== undefined),
	};
}

/** Print jobs, newest first, kept fresh by `printjob.*` events (P5-T26). */
export function usePrintJobs(
	filter: {
		printerId?: string;
		status?: PrintJob["status"][];
		/** One bill's or KOT's jobs, for its page. */
		refType?: "bill" | "kot";
		refId?: string;
	} = {},
) {
	const trpc = useTRPC();
	const query = useQuery(trpc.staff.printing.listJobs.queryOptions({ ...filter, limit: 100 }));
	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		jobs: query.data ?? [],
		retry: () => void query.refetch(),
	};
}

export type PrintAction = "print" | "retry" | "handled";

/**
 * Print and reprint (OP-14, P5-T26) and what a person does with a job that
 * did not print (P5-T23): Retry, Print from this device, Mark as handled.
 * A bill needs `bill.reprint`, a KOT `kot.update`.
 */
export function usePrintActions() {
	const core = useStaffCore();
	const current = useCurrentMembership();
	const canReprintBill = useCan("bill.reprint");
	const canReprintKot = useCan("kot.update");
	const timeZone = current.data?.restaurant.timezone ?? "Asia/Kolkata";
	const [busy, setBusy] = useState<{ action: PrintAction; id: string } | null>(null);
	const [problem, setProblem] = useState<{
		action: PrintAction;
		id: string;
		problem: PrintProblem;
	} | null>(null);
	const [outcome, setOutcome] = useState<{ id: string; outcome: PrintOutcome["kind"] } | null>(
		null,
	);

	const run = useCallback(
		async <T>(action: PrintAction, id: string, call: () => Promise<T>): Promise<T | null> => {
			setProblem(null);
			setOutcome(null);
			setBusy({ action, id });
			try {
				return await call();
			} catch (error) {
				setProblem({ action, id, problem: printProblemOf(error) });
				return null;
			} finally {
				setBusy(null);
			}
		},
		[],
	);
	const printing = useCallback(
		async (id: string, call: () => Promise<PrintOutcome>) => {
			const result = await run("print", id, call);
			if (result) setOutcome({ id, outcome: result.kind });
			return result;
		},
		[run],
	);

	return {
		canReprintBill,
		canReprintKot,
		busy,
		problem,
		/** "printedOffline" or "offline" after a print without the API. */
		outcome,
		clear: useCallback(() => {
			setProblem(null);
			setOutcome(null);
		}, []),
		/** Prints a bill again, marked "Duplicate". Offline, it prints from the bill on the device. */
		printBill: useCallback(
			(bill: Bill, printerId?: string) =>
				printing(bill.id, () =>
					core.print.print(
						{ kind: "bill", refId: bill.id, ...(printerId ? { printerId } : {}) },
						() =>
							billDocument(bill.snapshot, {
								copyLabel: "Duplicate",
								billId: bill.id,
								payments: bill.payments
									.filter((p) => p.status === "confirmed")
									.map((p) => ({ method: p.method, amountPaise: p.amountPaise })),
								logoFileId: current.data?.restaurant.logoFileId ?? null,
							}),
					),
				),
			[printing, core, current.data],
		),
		/** Prints a KOT again, marked "Duplicate". Offline, it prints from the KOT on the device. */
		printKot: useCallback(
			(kot: Kot) =>
				printing(kot.id, () =>
					core.print.print({ kind: "kot", refId: kot.id }, () => {
						const data = {
							kotNo: kot.kotNo,
							stationName: kot.stationName,
							tableLabel: kot.tableLabel,
							orderNo: kot.orderNo,
							createdAt: kot.createdAt,
							timeZone,
							waiterName: null,
							lines: kot.lines,
							copyLabel: "Duplicate",
						};
						return kot.kind === "cancel" ? kotCancelDocument(data) : kotDocument(data);
					}),
				),
			[printing, core, timeZone],
		),
		/** Queues a failed or handled job again; this device prints it when it can. */
		retry: useCallback(
			(job: PrintJob) => run("retry", job.id, () => core.print.retry(job)),
			[run, core],
		),
		/** "Print from this device": claims the job here (after Retry when it failed). */
		printHere: useCallback(
			(job: PrintJob) => run("print", job.id, () => core.print.retry(job)),
			[run, core],
		),
		canPrintHere: useCallback((printerId: string) => core.print.canPrintHere(printerId), [core]),
		markHandled: useCallback(
			(job: PrintJob) => run("handled", job.id, () => core.print.markHandled(job.id)),
			[run, core],
		),
	};
}

/**
 * Print jobs of an order's KOTs that need a person (P5-T23, printing.md,
 * Nobody can print): queued for 20 seconds with nobody printing them, or
 * failed. Shown on the order as "KOT not printed".
 */
export function useStuckPrintJobs(kotIds: readonly string[], since: string | null) {
	const trpc = useTRPC();
	const now = useNow();
	const enabled = kotIds.length > 0 && since !== null;
	const query = useQuery({
		...trpc.staff.printing.listJobs.queryOptions({
			refType: "kot",
			status: ["queued", "failed"],
			...(since ? { since } : {}),
			limit: 100,
		}),
		enabled,
		// The 20 seconds pass without an event: look again now and then.
		refetchInterval: enabled ? 10_000 : false,
	});
	const ids = useMemo(() => new Set(kotIds), [kotIds]);
	return useMemo(
		() =>
			(query.data ?? []).filter(
				(job) =>
					job.refId !== null &&
					ids.has(job.refId) &&
					(job.status === "failed" ||
						now - new Date(job.queuedAt).getTime() >= PRINT_NOT_PRINTED_AFTER_MS),
			),
		[query.data, ids, now],
	);
}
